import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Fingerprint, KeyRound } from 'lucide-react';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { registerStepUpController, StepUpCancelledError } from '../api/stepUp';
import { stepUpWebauthnOptions, stepUpWebauthnVerify, stepUpTotp } from '../api/mfa';
import { ApiError } from '../api/client';
import type { StepUpTier, StepUpAction } from '../api/types';
import { useFocusTrap } from '../lib/useFocusTrap';
import { SUPPORT_EMAIL } from '../lib/support';
import { useAuth } from '../auth/useAuth';

const ICON_STROKE = 1.75;

const SHELL_BASE = 'fixed inset-0 z-[70] flex items-center justify-center px-4';
// Opaque, not a scrim: nothing behind it is ready, so showing a hint of the skeleton shell
// would be suggesting content that cannot arrive. Scrolls, since it owns the whole viewport.
const SHELL_SESSION_ENTRY = 'overflow-y-auto bg-cream py-10';
const SHELL_IN_CONTEXT = '';

const CARD_BASE = 'relative w-full max-w-[400px] rounded-token border border-line bg-surface p-6';
// Flatter: it sits on its own background rather than floating above the app.
const CARD_SESSION_ENTRY = 'shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)]';
const CARD_IN_CONTEXT = 'shadow-[0_20px_50px_-12px_rgba(15,23,42,0.3)]';

type Phase =
  | 'choose'     // offering "verify with passkey" (if supported) + "use a code"
  | 'passkey'    // mid-ceremony: awaiting the browser's WebAuthn prompt
  | 'totp'       // asking for a 6-digit authenticator code
  | 'submitting'; // TOTP code in flight

interface PendingRequest {
  tier: StepUpTier | undefined;
  action: StepUpAction | undefined;
  token: string;
  /** True for the floor presentation: the full-screen, non-dismissible wall. Set when the
   *  ceremony opens on a `'floor'` reason, or later by `escalate`. See the F18 note below. */
  sessionEntry: boolean;
  resolve: () => void;
  reject: (err: Error) => void;
}

/**
 * The session-entry line has to be true on BOTH occasions a floor refusal arrives: a fresh
 * sign-in, and a mid-session re-lock when the ~12h view assurance lapses over a page the user
 * already has open. An earlier draft said "before we open your workspace", which is plainly
 * false in the second case — and the two are deliberately indistinguishable here, because the
 * server tells us only that the floor refused, not how long ago it stopped being satisfied.
 */
export function tierCopy(tier: StepUpTier | undefined, sessionEntry: boolean, action?: StepUpAction): string {
  // Kept under ~50 characters on purpose: the card is max-w-[400px] with p-6, so at
  // text-[13px] a longer line wraps and leaves a lone word on the second row.
  if (sessionEntry) return "We need to confirm it’s you to protect your data.";
  if (tier === 'reveal' && action === 'save') return "For your security, saving this needs a fresh verification.";
  if (tier === 'reveal' && action === 'remove') return "For your security, removing this needs a fresh verification.";
  if (tier === 'reveal') return "For your security, revealing this needs a fresh verification.";
  return "For your security, viewing this needs a quick verification.";
}

/**
 * Mounted in two places as read on 2026-10-07: in ProtectedRoute (above the
 * workspace/router tree) for every signed-in app session, and by the firm's snapshot form
 * (`intake/IntakeFlow.tsx`), which is outside ProtectedRoute and mounts its own. The two
 * are separate routes in `router.tsx`, so a page renders one of them. Implements the {@link StepUpController} interface that
 * `api/stepUp.ts`'s `runStepUpCeremony` calls into — this is the ONLY piece
 * that actually shows UI; the interceptor in api/client.ts knows nothing about
 * passkeys, TOTP, or modals, only that it should await a promise.
 *
 * Prefers the passkey ceremony (a single confirming click, then the browser's
 * native prompt); falls back to a TOTP code automatically on `404 NO_PASSKEY`,
 * or immediately if this browser doesn't support WebAuthn at all.
 *
 * F18 — TWO OCCASIONS, TWO PRESENTATIONS. The ceremony arises in two situations
 * that look identical to this component but are opposite to the user:
 *
 *   IN CONTEXT (`sessionEntry: false`) — the user holds a populated page and
 *   deliberately hit a gate. A dismissible modal is correct; cancelling fails
 *   that one request and returns them to work they can still see.
 *
 *   SESSION ENTRY (`sessionEntry: true`) — they just signed in and the workspace
 *   assurance floor is refusing everything, so every pillar fetch 403s and the
 *   shell shows skeletons that CANNOT resolve. Here a dismissible modal is a
 *   security gate that can be clicked into a dead end: not signed out, not
 *   authorized, permanent shimmer, no way forward, and the prompt only returns
 *   on a navigation attempt. So this case is full-screen and non-dismissible —
 *   no close button, no backdrop dismiss — with sign-out as the deliberate way
 *   out. Backing out must land somewhere coherent, never on a skeleton.
 *
 * The signal is the 403's own `reason` (appcore docs/28 §9): `'floor'` means the
 * workspace assurance floor refused, so EVERY workspace-scoped call will refuse and
 * the shell cannot populate. NOT the tier — a `view` gate trips on either occasion.
 *
 * An earlier version inferred this client-side from observed HTTP history ("has any
 * workspace-scoped request succeeded?"). That needed four rounds of fixes — a path
 * allowlist, an exception list, an epoch counter, a reset hook — each round finding a
 * real bug, and the allowlist itself was wrong five times in both directions. The fact
 * is known with certainty at the server's throw site, so it is sent rather than
 * reconstructed. Absent (older backend) reads as `'operation'`, the safe default.
 *
 * Captured once when the ceremony opens; joiners share it, which is right because they
 * share the ceremony (see runStepUpCeremony's coalescing). The one exception moves toward
 * the wall only: a `'floor'` joiner escalates an in-context prompt to session entry.
 *
 * `reason` is the PRESENTATION reason, not always the wire's (steward-app#1529): the API
 * client hands over a floor refusal as `'operation'` only for a read that opted in
 * (`foreignFloor: 'in-context'`, the advisor roster's per-client card) and is bound to a
 * workspace other than the active one. That floor guards one figure on a page the user
 * still holds, not the session. See `stepUpPresentationReason` in api/client.ts.
 */
export function StepUpProvider({ children, workspaceExit = true, onSignOut }: {
  children: ReactNode;
  /**
   * Whether the session-entry wall offers "Choose a different workspace". `true` everywhere
   * inside the signed-in app. The firm's snapshot form (`intake/IntakeFlow.tsx`) mounts its
   * own provider with `false`: the person there is signing in to one firm's form and may hold
   * no workspace at all, so the link leads nowhere they can use. The mount site says which
   * it is; nothing here looks at the host or the path. "Sign out instead" is always offered,
   * so the wall keeps a way out.
   */
  workspaceExit?: boolean;
  /**
   * What "Sign out instead" does, when the mount site has its own way of leaving. Omitted
   * inside the signed-in app, where the button calls `useAuth().signOut` directly. The
   * firm's snapshot form passes its own sign-out, which does its tidying before it calls
   * the same `signOut`. When the prop is supplied this component only marks its button busy
   * and calls it; it does not call `signOut` itself, so ending the session is the mount
   * site's job. Both directions are pinned in `StepUpPresentation.test.tsx`, and what the
   * form's own sign-out does from this button in `intake/IntakeFlow.test.tsx`.
   */
  onSignOut?: () => void;
}) {
  const [pending, setPending] = useState<PendingRequest | null>(null);
  const [phase, setPhase] = useState<Phase>('choose');
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  // Sign-out can take up to ~3s on a cold discovery lookup, and this is the one screen where the
  // user is already stuck — silence there reads as a dead button.
  const [signingOut, setSigningOut] = useState(false);
  const { signOut } = useAuth();
  const requestRef = useRef<PendingRequest | null>(null);
  requestRef.current = pending;

  useEffect(() => {
    registerStepUpController({
      present: (token, tier, reason, action) =>
        new Promise<void>((resolve, reject) => {
          setError('');
          setCode('');
          setPhase(browserSupportsWebAuthn() ? 'choose' : 'totp');
          setPending({ tier, action, token, sessionEntry: reason === 'floor', resolve, reject });
        }),
      // A floor refusal joined a ceremony opened as in-context (runStepUpCeremony's header).
      // Same promise, same phase, same typed code: only the presentation moves to the wall.
      escalate: () => setPending(p => (p && !p.sessionEntry ? { ...p, sessionEntry: true } : p)),
    });
    return () => registerStepUpController(null);
  }, []);

  function finish(fn: () => void) {
    fn();
    setPending(null);
  }

  async function tryPasskey() {
    const req = requestRef.current;
    if (!req) return;
    setPhase('passkey');
    setError('');
    try {
      const optionsJSON = await stepUpWebauthnOptions(req.token);
      const response = await startAuthentication({ optionsJSON });
      await stepUpWebauthnVerify(req.token, { response });
      finish(req.resolve);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404 && err.code === 'NO_PASSKEY') {
        setPhase('totp');
        setError('');
        return;
      }
      setPhase('choose');
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'That was cancelled or timed out. Try again, or use a code instead.'
          : "We couldn’t verify with your passkey. Try again, or use a code instead.",
      );
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    const req = requestRef.current;
    if (!req || !code.trim()) return;
    setPhase('submitting');
    setError('');
    try {
      await stepUpTotp(req.token, { code: code.trim() });
      finish(req.resolve);
    } catch {
      setPhase('totp');
      setCode('');
      setError("That code didn’t match. Check your authenticator app and try again.");
    }
  }

  function cancel() {
    const req = requestRef.current;
    if (!req) return;
    finish(() => req.reject(new StepUpCancelledError()));
  }

  const trapRef = useFocusTrap<HTMLDivElement>(!!pending);
  // "to continue" only on session entry, where it names what is being gated: the workspace
  // itself, not a page the user can already see.
  const heading = pending?.sessionEntry ? 'Verify it’s you to continue' : 'Verify it’s you';

  return (
    <>
      {children}
      {pending && createPortal((
        <div
          // Base classes shared, only the variant delta differs — written as one string each so
          // a later padding/radius tweak cannot land on one presentation and miss the other.
          className={`${SHELL_BASE} ${pending.sessionEntry ? SHELL_SESSION_ENTRY : SHELL_IN_CONTEXT}`}
          role="dialog"
          aria-modal="true"
          aria-label={heading}
          ref={trapRef}
        >
          {/* Backdrop dismiss belongs only to the in-context case. On session entry there is
              nothing legitimate to dismiss back to. */}
          {!pending.sessionEntry && <div className="absolute inset-0 bg-ink/40" onClick={cancel} aria-hidden />}
          <div className={`${CARD_BASE} ${pending.sessionEntry ? CARD_SESSION_ENTRY : CARD_IN_CONTEXT}`}>
            {!pending.sessionEntry && (
              <button
                type="button"
                onClick={cancel}
                aria-label="Cancel"
                className="absolute right-3 top-3 flex h-8 w-8 max-sm:h-11 max-sm:w-11 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-slate transition-colors duration-150 hover:text-navy"
              >
                <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
              </button>
            )}

            <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-token bg-bronze/12 text-bronze">
              <Fingerprint size={18} strokeWidth={ICON_STROKE} aria-hidden />
            </div>
            {/* Same string the dialog announces via aria-label — review caught these drifting. */}
            <div className="mt-3 font-display text-[16px] font-semibold text-navy">{heading}</div>
            <div className="mb-4 mt-1 text-[13px] leading-relaxed text-slate font-sans">{tierCopy(pending.tier, pending.sessionEntry, pending.action)}</div>

            {(phase === 'choose' || phase === 'passkey') && (
              <div>
                <button
                  type="button"
                  onClick={() => void tryPasskey()}
                  disabled={phase === 'passkey'}
                  className="flex w-full items-center justify-center gap-2 px-5 py-2.5 max-sm:min-h-11 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-60"
                >
                  <Fingerprint size={15} strokeWidth={ICON_STROKE} aria-hidden />
                  {phase === 'passkey' ? 'Waiting for your passkey…' : 'Verify with your passkey'}
                </button>
                <button
                  type="button"
                  onClick={() => { setPhase('totp'); setError(''); }}
                  disabled={phase === 'passkey'}
                  className="mt-2.5 flex w-full items-center justify-center gap-1.5 px-4 py-2 max-sm:min-h-11 text-[11px] font-semibold rounded-token border border-line-strong cursor-pointer bg-transparent text-navy uppercase tracking-[0.06em] font-sans disabled:opacity-60"
                >
                  <KeyRound size={14} strokeWidth={ICON_STROKE} aria-hidden />
                  Use an authenticator code instead
                </button>
              </div>
            )}

            {(phase === 'totp' || phase === 'submitting') && (
              <form onSubmit={e => void submitTotp(e)}>
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate font-sans" htmlFor="stepup-totp-code">
                  Code from your authenticator app
                </label>
                <input
                  id="stepup-totp-code"
                  className="w-full px-3 py-2 text-[16px] tracking-[0.3em] text-center border border-line-strong rounded-token box-border bg-surface text-navy font-sans outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  disabled={phase === 'submitting'}
                />
                <button
                  type="submit"
                  disabled={phase === 'submitting' || code.trim().length < 6}
                  className="mt-3 w-full px-5 py-2.5 max-sm:min-h-11 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {phase === 'submitting' ? 'Verifying…' : 'Verify'}
                </button>
                {browserSupportsWebAuthn() && (
                  <button
                    type="button"
                    onClick={() => { setPhase('choose'); setError(''); }}
                    disabled={phase === 'submitting'}
                    className="mt-2 w-full px-4 py-1.5 max-sm:min-h-11 text-[11px] font-semibold cursor-pointer bg-transparent border-none text-slate font-sans hover:text-navy disabled:opacity-60"
                  >
                    Use your passkey instead
                  </button>
                )}
              </form>
            )}

            {error && <div className="mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{error}</div>}

            {/* The deliberate ways out of a non-dismissible gate. Without one the screen is a
                trap; with a Cancel it is the dead end again.

                Both are needed. The floor is per WORKSPACE, so a multi-workspace user whose
                last-used workspace is floored, and who cannot finish the ceremony (phone lost,
                passkey on another device), would otherwise only have sign-out — and signing back
                in re-resolves the same stored workspace into the same gate, which is a loop.
                /select-workspace makes no workspace-scoped calls, so it renders through a floor. */}
            {pending.sessionEntry && (
              <div className="mt-4 flex flex-col gap-1">
                {workspaceExit && (
                  <a
                    href="/select-workspace"
                    className="w-full px-4 py-1.5 max-sm:flex max-sm:min-h-11 max-sm:items-center max-sm:justify-center text-center text-[11px] font-semibold text-slate font-sans no-underline hover:text-navy"
                  >
                    Choose a different workspace
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => { setSigningOut(true); if (onSignOut) onSignOut(); else void signOut(); }}
                  disabled={signingOut}
                  className="w-full cursor-pointer border-none bg-transparent px-4 py-1.5 max-sm:min-h-11 text-[11px] font-semibold text-slate font-sans hover:text-navy disabled:opacity-60"
                >
                  {signingOut ? 'Signing out…' : 'Sign out instead'}
                </button>
              </div>
            )}

            <p className="m-0 mt-5 text-[11px] leading-relaxed text-slate font-sans">
              Lost access to your passkey and your authenticator app? Contact us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-navy underline">{SUPPORT_EMAIL}</a>{' '}
              and we’ll help you back in.
            </p>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
